import React from 'react';
import { PricingJob } from '../../api/pricing';
import { colors, spacing, typography, borderRadius } from '../../styles/tokens';
import { formatInstantDate } from '../../utils/datetime';

interface RepricingJobTableProps {
  jobs: PricingJob[];
  isLoading?: boolean;
  onJobClick?: (job: PricingJob) => void;
  onAction?: (jobId: string, action: string) => void;
}

const STATUS_COLORS: Record<string, string> = {
  queued: colors.info,
  running: colors.warning,
  completed: colors.success,
  failed: colors.error,
  cancelled: colors.textSecondary,
  pending_retry: colors.warning,
  paused: colors.info,
  expired: colors.error,
  committed: colors.success,
  discarded: colors.textSecondary,
};

const STATUS_LABELS: Record<string, string> = {
  queued: 'Queued',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
  pending_retry: 'Pending Retry',
  paused: 'Paused',
  expired: 'Expired',
  committed: 'Committed',
  discarded: 'Discarded',
};

export function RepricingJobTable({
  jobs,
  isLoading = false,
  onJobClick,
  onAction,
}: RepricingJobTableProps) {
  const styles = {
    container: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.md,
      overflow: 'hidden',
      border: `1px solid ${colors.border}`,
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const,
      fontSize: typography.body.fontSize,
    },
    headerRow: {
      backgroundColor: colors.background,
      borderBottom: `2px solid ${colors.border}`,
    },
    headerCell: {
      padding: spacing.md,
      textAlign: 'left' as const,
      fontWeight: typography.h2.fontWeight as any,
      color: colors.text,
      fontSize: typography.small.fontSize,
    },
    row: {
      borderBottom: `1px solid ${colors.border}`,
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
    },
    rowHover: {
      backgroundColor: colors.background,
    },
    cell: {
      padding: spacing.md,
      color: colors.text,
    },
    statusBadge: {
      display: 'inline-block',
      padding: `${spacing.xs} ${spacing.sm}`,
      borderRadius: borderRadius.sm,
      fontSize: typography.small.fontSize,
      fontWeight: 500,
      textAlign: 'center' as const,
    },
    actionButton: {
      padding: `${spacing.xs} ${spacing.sm}`,
      marginRight: spacing.xs,
      fontSize: typography.small.fontSize,
      border: `1px solid ${colors.border}`,
      borderRadius: borderRadius.sm,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    emptyState: {
      padding: spacing['2xl'],
      textAlign: 'center' as const,
      color: colors.textSecondary,
    },
    loadingState: {
      padding: spacing['2xl'],
      textAlign: 'center' as const,
      color: colors.textSecondary,
    },
  };

  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingState}>Loading jobs...</div>
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <p>No repricing jobs yet</p>
          <p style={{ fontSize: typography.small.fontSize, marginTop: spacing.sm }}>
            Use Manapool bulk reprice or a sync above
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <table style={styles.table}>
        <thead>
          <tr style={styles.headerRow}>
            <th style={styles.headerCell}>Job Name</th>
            <th style={styles.headerCell}>Status</th>
            <th style={styles.headerCell}>Variants</th>
            <th style={styles.headerCell}>Success</th>
            <th style={styles.headerCell}>Errors</th>
            <th style={styles.headerCell}>Created</th>
            <th style={styles.headerCell}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => (
            <tr
              key={job.id}
              style={styles.row}
              onClick={() => onJobClick?.(job)}
            >
              <td style={styles.cell}>
                {job.job_name || `Job ${job.id.slice(0, 8)}`}
              </td>
              <td style={styles.cell}>
                <div
                  style={{
                    ...styles.statusBadge,
                    backgroundColor: STATUS_COLORS[job.status],
                    color: 'white',
                  }}
                >
                  {STATUS_LABELS[job.status]}
                </div>
              </td>
              <td style={styles.cell}>{job.variant_count}</td>
              <td style={styles.cell}>
                <span style={{ color: colors.success, fontWeight: 500 }}>
                  {job.success_count}
                </span>
              </td>
              <td style={styles.cell}>
                <span style={{ color: job.error_count > 0 ? colors.error : colors.textSecondary }}>
                  {job.error_count}
                </span>
              </td>
              <td style={styles.cell}>
                {formatInstantDate(job.created_at)}
              </td>
              <td style={styles.cell}>
                <button
                  style={{
                    ...styles.actionButton,
                    backgroundColor: colors.primary,
                    color: 'white',
                    borderColor: colors.primary,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAction?.(job.id, 'view');
                  }}
                >
                  View
                </button>
                {(job.status === 'running' || job.status === 'queued') && (
                  <button
                    style={{
                      ...styles.actionButton,
                      backgroundColor: colors.error,
                      color: 'white',
                      borderColor: colors.error,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAction?.(job.id, 'cancel');
                    }}
                  >
                    Cancel
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
